import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Archive, Calendar, User, DollarSign, TrendingUp, 
  ArrowLeft, RotateCcw, Eye
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ArchivePage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allowedStatuses, setAllowedStatuses] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchArchivedProjects();
    fetchAllowedStatuses();
  }, []);

  const fetchArchivedProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects?archived_only=true`);
      setProjects(response.data);
    } catch (error) {
      toast.error('Błąd ładowania archiwum');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllowedStatuses = async () => {
    try {
      const response = await axios.get(`${API_URL}/user-status-permissions`);
      setAllowedStatuses(response.data.allowed_statuses || []);
    } catch (error) {
      console.error('Error fetching status permissions:', error);
    }
  };

  const handleRestore = async (project, newStatus = 'lead') => {
    if (!allowedStatuses.includes(newStatus)) {
      toast.error(`Brak uprawnień do przywracania zleceń`);
      return;
    }

    try {
      const updateData = {
        nazwa: project.nazwa,
        klient: project.klient,
        adres_inwestycji: project.adres_inwestycji,
        telefon_klienta: project.telefon_klienta,
        email_klienta: project.email_klienta,
        total_offer: project.total_offer,
        costs: project.costs,
        status: newStatus,
        description: project.description,
        owner_id: project.owner_id
      };

      await axios.patch(`${API_URL}/projects/${project.id}`, updateData);
      toast.success('Zlecenie przywrócone');
      fetchArchivedProjects();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Błąd przywracania zlecenia';
      toast.error(errorMessage);
    }
  };

  const showFinancials = user?.role !== 'montaz' && user?.role !== 'guest';

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight flex items-center gap-3">
              <Archive className="w-8 h-8 md:w-10 md:h-10 text-gray-500" />
              Archiwum
            </h1>
            <p className="text-[var(--crm-text-secondary)] mt-1">
              Zlecenia ze statusem "Brak akceptacji" ({projects.length})
            </p>
          </div>
          
          <Link to="/kanban">
            <Button 
              variant="outline"
              className="bg-transparent border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)] hover:text-[var(--crm-text-primary)]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Powrót do Tablicy
            </Button>
          </Link>
        </div>

        {/* Info */}
        <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-4 mb-6">
          <p className="text-gray-300 text-sm">
            Zlecenia w archiwum są wykluczone z obliczeń marży i zysku na dashboardzie.
            Możesz przywrócić zlecenie klikając "Przywróć".
          </p>
        </div>

        {projects.length === 0 ? (
          <Card className="bg-[var(--crm-bg-card)] border-[var(--crm-border)] p-8 text-center">
            <Archive className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-[var(--crm-text-primary)] text-lg font-medium mb-2">Archiwum jest puste</h3>
            <p className="text-[var(--crm-text-secondary)]">Brak zleceń ze statusem "Brak akceptacji"</p>
          </Card>
        ) : (
          <Card className="bg-[var(--crm-bg-card)] border-[var(--crm-border)]">
            <Table>
              <TableHeader>
                <TableRow className="border-[var(--crm-border)] hover:bg-transparent">
                  <TableHead className="text-[var(--crm-text-secondary)]">Nazwa</TableHead>
                  <TableHead className="text-[var(--crm-text-secondary)]">Klient</TableHead>
                  {showFinancials && (
                    <>
                      <TableHead className="text-[var(--crm-text-secondary)]">Oferta</TableHead>
                      <TableHead className="text-[var(--crm-text-secondary)]">Marża</TableHead>
                    </>
                  )}
                  <TableHead className="text-[var(--crm-text-secondary)]">Data utworzenia</TableHead>
                  <TableHead className="text-[var(--crm-text-secondary)]">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((project) => (
                  <TableRow 
                    key={project.id} 
                    className="border-[var(--crm-border)] hover:bg-[var(--crm-bg-panel)]"
                  >
                    <TableCell className="text-[var(--crm-text-primary)] font-medium">
                      {project.nazwa}
                    </TableCell>
                    <TableCell className="text-[var(--crm-text-secondary)]">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {project.klient}
                      </div>
                    </TableCell>
                    {showFinancials && (
                      <>
                        <TableCell className="text-[var(--crm-text-secondary)]">
                          {project.total_offer?.toLocaleString('pl-PL')} zł
                        </TableCell>
                        <TableCell>
                          <span className={`${project.margin_percent >= 20 ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {project.margin_percent?.toFixed(1)}%
                          </span>
                        </TableCell>
                      </>
                    )}
                    <TableCell className="text-[var(--crm-text-secondary)]">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {new Date(project.created_at).toLocaleDateString('pl-PL')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/projects/${project.id}`)}
                          className="bg-transparent border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)]"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {allowedStatuses.includes('lead') && (
                          <Button
                            size="sm"
                            onClick={() => handleRestore(project, 'lead')}
                            className="bg-blue-600 hover:bg-blue-700 text-[var(--crm-text-primary)]"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Przywróć
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </Layout>
  );
};
